import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/context/auth-context";
import { useToast } from "@/components/ui/use-toast";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Award, FileText, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCEU } from "@/lib/utils";

interface Certificate {
  id: string;
  course_id: string;
  issue_date: string;
  user_id: string;
  course_title: string;
  hours: number;
}

export default function BosaRequest() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [selectedCertificateId, setSelectedCertificateId] = useState<string>('');
  const [userNotes, setUserNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  // Fetch user's existing certificates
  const { data: certificates = [], isLoading } = useQuery({
    queryKey: ['user-certificates'],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('certificates')
        .select(`
          id,
          course_id,
          issue_date,
          user_id,
          courses:course_id (
            title,
            hours
          )
        `)
        .eq('user_id', user.id);
        
      if (error) {
        throw error;
      }
      
      return data.map((cert: any) => ({
        id: cert.id,
        course_id: cert.course_id,
        issue_date: cert.issue_date,
        user_id: cert.user_id,
        course_title: cert.courses?.title || 'Unknown Course',
        hours: cert.courses?.hours || 0
      })) as Certificate[];
    },
    enabled: !!user?.id,
  });
  
  // Check if the certificate already has a BOSA request
  const { data: existingRequests = [], isLoading: isLoadingRequests } = useQuery({
    queryKey: ['existing-bosa-requests'],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('bosa_certificate_requests')
        .select('certificate_id, status')
        .eq('user_id', user.id);
        
      if (error) {
        throw error;
      }
      
      return data;
    },
    enabled: !!user?.id,
  });
  
  // Filter out certificates that already have pending or approved BOSA requests
  const availableCertificates = certificates.filter(cert => {
    const existingRequest = existingRequests.find(req => req.certificate_id === cert.id);
    return !existingRequest || existingRequest.status === 'rejected';
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCertificateId) {
      toast({
        title: "Error",
        description: "Please select a certificate",
        variant: "destructive",
      });
      return;
    }
    
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('bosa_certificate_requests')
        .insert({
          certificate_id: selectedCertificateId,
          user_id: user.id,  // Make sure to include user_id
          user_notes: userNotes,
          request_date: new Date().toISOString(),
          status: 'pending'
        });
        
      if (error) throw error;
      
      setSuccessDialogOpen(true);
      setUserNotes('');
      setSelectedCertificateId('');
    } catch (error) {
      console.error("Error submitting BOSA request:", error);
      toast({
        title: "Error",
        description: "Failed to submit your BOSA certificate request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const selectedCertificate = certificates.find(cert => cert.id === selectedCertificateId);

  return (
    <div className="w-full py-1 px-1 space-y-4 animate-fade-in">
      <Button 
        variant="outline" 
        onClick={() => navigate(-1)} 
        className="mb-4 font-body border-primary/30 text-primary hover:bg-accent-subtle"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>
      
      <div className="page-header">
        <div className="flex items-center gap-2">
          <Award className="h-6 w-6 text-[#004161]" />
          <h1>Request BOSA Certificate</h1>
        </div>
        <p>Convert your CEU certificate to a BOSA certificate for license renewal</p>
      </div>
      
      <Card className="border-secondary/20 shadow-md">
        <CardHeader className="bg-gradient-to-r from-white to-accent-subtle/30">
          <CardTitle className="font-title text-primary">BOSA Certificate Request</CardTitle>
          <CardDescription className="font-body">
            Select a certificate and provide any additional information needed for your BOSA certificate request
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-sm font-medium font-section text-primary">Select Certificate</h3>
              {isLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : availableCertificates.length === 0 ? (
                <div className="text-sm text-muted-foreground font-body bg-accent-subtle/20 p-4 rounded-md">
                  You do not have any certificates eligible for BOSA conversion. Complete courses to earn certificates.
                </div>
              ) : (
                <Select 
                  value={selectedCertificateId} 
                  onValueChange={setSelectedCertificateId}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="w-full font-body border-accent-light/30 focus:ring-primary/20">
                    <SelectValue placeholder="Select a certificate" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCertificates.map((cert) => (
                      <SelectItem key={cert.id} value={cert.id} className="font-body">
                        {cert.course_title} ({formatCEU(cert.hours)} CEUs)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {selectedCertificate && (
              <div className="p-4 border rounded-md bg-accent/10 border-accent/20">
                <h3 className="text-sm font-medium font-section text-primary mb-2">Certificate Details</h3>
                <div className="space-y-1 text-sm font-body">
                  <p><span className="font-medium text-primary">Course:</span> {selectedCertificate.course_title}</p>
                  <p><span className="font-medium text-primary">CEUs:</span> {formatCEU(selectedCertificate.hours)}</p>
                  <p><span className="font-medium text-primary">Completion Date:</span> {new Date(selectedCertificate.issue_date).toLocaleDateString()}</p>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <h3 className="text-sm font-medium font-section text-primary">Additional Notes (Optional)</h3>
              <Textarea
                placeholder="Provide any additional information that might help with your BOSA certificate request"
                className="min-h-[100px] font-body border-accent-light/30 focus:ring-primary/20"
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </CardContent>
          <CardFooter className="bg-muted/20">
            <Button 
              type="submit" 
              disabled={!selectedCertificateId || isSubmitting}
              className="w-full md:w-auto font-body bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? "Submitting..." : "Submit BOSA Request"}
            </Button>
          </CardFooter>
        </form>
      </Card>
      
      <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-title text-primary">Request Submitted Successfully</DialogTitle>
            <DialogDescription className="font-body">
              Your BOSA certificate request has been submitted and is pending review.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 border rounded-md bg-accent/10 text-accent border-accent/20 font-body text-sm">
            <p>Our staff will review your request and process your BOSA certificate. You will be notified when it is ready.</p>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button 
              variant="outline" 
              onClick={() => navigate('/certificates')} 
              className="font-body border-primary/30 hover:bg-accent-subtle"
            >
              <FileText className="mr-2 h-4 w-4" />
              Go to Certificates
            </Button>
            <Button 
              onClick={() => setSuccessDialogOpen(false)} 
              className="font-body bg-primary hover:bg-primary/90"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
